import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PageShell from "../components/PageShell";
import { SparklesIcon, UsersIcon, CalendarIcon } from "../components/Icons";
import "./Dashboard.css";

const Dashboard = () => {
  const navigate = useNavigate();
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [greetingsSent, setGreetingsSent] = useState(0);
  const [employeeHistory, setEmployeeHistory] = useState([]);
  const [upcomingFestivals, setUpcomingFestivals] = useState([]);
  const [nudges, setNudges] = useState([]);
  const [upcomingAnniversaries, setUpcomingAnniversaries] = useState([]);
  const [kudosToday, setKudosToday] = useState(0);
  const [kudosWeek, setKudosWeek] = useState(0);
  const [topPraisedUsers, setTopPraisedUsers] = useState([]);
  const [topPraisedUnits, setTopPraisedUnits] = useState([]);
  const [kudosUnitCode, setKudosUnitCode] = useState("");
  const [kudosAvailableUnits, setKudosAvailableUnits] = useState([]);
  const [showIntroPopup, setShowIntroPopup] = useState(false);

  useEffect(() => {
    fetchTotalEmployees();
    fetchGreetingsSent();
    fetchEmployeeHistory();
    fetchUpcomingFestivals();
    fetchNudges();
    fetchUpcomingAnniversaries();
    fetchKudosAnalytics();
    fetchOrgUnitsForFilter();

    if (!localStorage.getItem("hasSeenNudgeIntro")) {
      setShowIntroPopup(true);
    }
  }, []);

  const closeIntroPopup = () => {
    localStorage.setItem("hasSeenNudgeIntro", "true");
    setShowIntroPopup(false);
  };

  const handleNudge = (nudge) => {
    if (nudge.action === "send_greeting") {
      navigate(`/ai-wishes?festival=${encodeURIComponent(nudge.holiday_name)}&recipient_id=${nudge.report_id}`);
    } else if (nudge.action === "approve_memory") {
      navigate(`/memories?space=pending`);
    }
  };

  const fetchNudges = async () => {
    try {
      const response = await axios.get("/api/manager/nudges", { withCredentials: true });
      setNudges(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching nudges:", error);
      setNudges([]);
    }
  };

  const fetchUpcomingAnniversaries = async () => {
    try {
      const response = await axios.get("/api/anniversaries/upcoming", {
        withCredentials: true,
      });
      const items = Array.isArray(response.data?.items) ? response.data.items : [];
      setUpcomingAnniversaries(items);
    } catch (error) {
      console.error("Error fetching upcoming anniversaries:", error);
      setUpcomingAnniversaries([]);
    }
  };

  const handleAnniversaryGreeting = (anniversary) => {
    const params = new URLSearchParams({
      festival: "Work Anniversary",
      recipient_id: anniversary.user_id,
      years_of_service: String(anniversary.years_of_service),
    });
    navigate(`/ai-wishes?${params.toString()}`);
  };

  const formatAnniversaryDate = (iso) => {
    try {
      const d = new Date(`${iso}T00:00:00`);
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } catch {
      return iso;
    }
  };

  const fetchTotalEmployees = async () => {
    try {
      const response = await axios.get("/api/directory", { withCredentials: true });
      setTotalEmployees(Array.isArray(response.data) ? response.data.length : 0);
    } catch (error) {
      console.error("Error fetching employees:", error);
      setTotalEmployees(0);
    }
  };

  const fetchGreetingsSent = async () => {
    try {
      const response = await axios.get("/api/greetings-sent");
      setGreetingsSent(response.data?.count || 0);
    } catch (error) {
      console.error("Error fetching greetings sent:", error);
      setGreetingsSent(0);
    }
  };

  const fetchEmployeeHistory = async () => {
    try {
      const response = await axios.get("/api/employees/history");
      setEmployeeHistory(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching employee history:", error);
      setEmployeeHistory([]);
    }
  };

  const fetchUpcomingFestivals = async () => {
    try {
      // If your backend has a festivals endpoint, keep it. Otherwise, fallback is OK.
      const response = await axios.get("/api/festivals/upcoming");
      setUpcomingFestivals(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      // Graceful fallback: keep empty list.
      setUpcomingFestivals([]);
    }
  };

  const fetchOrgUnitsForFilter = async () => {
    try {
      const response = await axios.get("/api/admin/org-units", {
        withCredentials: true,
        params: { include_members: 0 },
      });
      const rows = Array.isArray(response.data?.items) ? response.data.items : [];
      const normalized = rows
        .map((u) => ({
          code: String(u?.code || "").trim(),
          name: String(u?.name || "").trim(),
        }))
        .filter((u) => u.code);
      setKudosAvailableUnits(normalized);
    } catch (_error) {
      // Non-admin users may not have access; keep free-text fallback.
      setKudosAvailableUnits([]);
    }
  };

  const fetchKudosAnalytics = async (unitCode = "") => {
    try {
      const params = {};
      if ((unitCode || "").trim()) params.org_unit_code = unitCode.trim();
      const response = await axios.get("/api/kudos/analytics", { withCredentials: true, params });
      setKudosToday(Number(response.data?.today_count || 0));
      setKudosWeek(Number(response.data?.week_count || 0));
      setTopPraisedUsers(Array.isArray(response.data?.top_users) ? response.data.top_users : []);
      setTopPraisedUnits(Array.isArray(response.data?.top_units) ? response.data.top_units : []);
    } catch (error) {
      setKudosToday(0);
      setKudosWeek(0);
      setTopPraisedUsers([]);
      setTopPraisedUnits([]);
    }
  };

  const chartData = useMemo(() => {
    // Expecting [{ month: 'Aug', count: 2 }, ...]
    if (Array.isArray(employeeHistory) && employeeHistory.length > 0) return employeeHistory;

    // Default: last 6 months placeholders
    const now = new Date();
    const months = [];
    for (let i = 5; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        month: d.toLocaleDateString("en-US", { month: "short" }),
        count: 0,
      });
    }
    return months;
  }, [employeeHistory]);

  const maxCount = useMemo(() => {
    const m = Math.max(...chartData.map((d) => Number(d.count) || 0), 0);
    return m === 0 ? 1 : m;
  }, [chartData]);

  return (
    <PageShell
      title="Dashboard"
      subtitle="Workspace overview and upcoming moments."
    >
      <div className="dashMetrics">
        <div className="card">
          <div className="card__body metric">
            <div className="metric__icon" aria-hidden="true">
              <UsersIcon className="icon" />
            </div>
            <div className="metric__meta">
              <div className="metric__value">{totalEmployees}</div>
              <div className="metric__label">Total employees</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card__body metric">
            <div className="metric__icon" aria-hidden="true">
              <SparklesIcon className="icon" />
            </div>
            <div className="metric__meta">
              <div className="metric__value">{greetingsSent}</div>
              <div className="metric__label">AI greetings sent</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card__body metric metric--compact">
            <div className="metric__meta">
              <div className="metric__label">Adoption trend</div>
              <div className="metric__hint">Employees added in the last 6 months</div>
            </div>
            <div className="miniChart" aria-hidden="true">
              <svg width="100%" height="78" viewBox="0 0 480 220" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="dashLineGradient" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="rgba(16,185,129,0.35)" />
                    <stop offset="100%" stopColor="rgba(16,185,129,0.05)" />
                  </linearGradient>
                </defs>

                {/* grid */}
                <g className="chartGrid">
                  {[0, 1, 2, 3].map((i) => (
                    <line
                      key={i}
                      x1="40"
                      y1={40 + i * 40}
                      x2="460"
                      y2={40 + i * 40}
                      className="chartGrid__line"
                    />
                  ))}
                </g>

                {/* line */}
                <polyline
                  points={chartData
                    .map((d, idx) => {
                      const x = 40 + idx * (420 / (chartData.length - 1));
                      const y = 200 - 40 - ((Number(d.count) || 0) / maxCount) * 120;
                      return `${x},${y}`;
                    })
                    .join(" ")}
                  fill="none"
                  stroke="rgba(16,185,129,0.9)"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />

                {/* points */}
                {chartData.map((d, idx) => {
                  const x = 40 + idx * (420 / (chartData.length - 1));
                  const y = 200 - 40 - ((Number(d.count) || 0) / maxCount) * 120;
                  return (
                    <g key={idx}>
                      <circle cx={x} cy={y} r="5" className="chartPoint" />
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card__body metric">
            <div className="metric__icon" aria-hidden="true">
              <SparklesIcon className="icon" />
            </div>
            <div className="metric__meta">
              <div className="metric__value">{kudosToday}</div>
              <div className="metric__label">Kudos today</div>
              <div className="metric__hint">This week: {kudosWeek}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid-2" style={{ marginTop: 14 }}>
        <div className="card">
          <div className="card__header">
            <h2 className="card__title">Top praised people</h2>
          </div>
          <div className="card__body">
            {topPraisedUsers.length === 0 ? (
              <div className="emptyState">No kudos yet.</div>
            ) : (
              <div className="kudosList">
                {topPraisedUsers.map((u, idx) => (
                  <div key={`${u.name}-${idx}`} className="kudosRow">
                    <span>{u.name}</span>
                    <span className="pill">{u.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card__header">
            <h2 className="card__title">Top praised B360Us</h2>
          </div>
          <div className="card__body">
            {topPraisedUnits.length === 0 ? (
              <div className="emptyState">No B360U kudos yet.</div>
            ) : (
              <div className="kudosList">
                {topPraisedUnits.map((u, idx) => (
                  <div key={`${u.name}-${idx}`} className="kudosRow">
                    <span>{u.name}</span>
                    <span className="pill">{u.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <div className="card__body" style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end" }}>
          <div style={{ minWidth: 240 }}>
            <label className="label">Filter by B360U code</label>
            {kudosAvailableUnits.length > 0 ? (
              <select
                className="input"
                value={kudosUnitCode}
                onChange={(e) => setKudosUnitCode(e.target.value)}
              >
                <option value="">All B360Us</option>
                {kudosAvailableUnits.map((u) => (
                  <option key={u.code} value={u.code}>
                    {u.code}{u.name ? ` - ${u.name}` : ""}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className="input"
                value={kudosUnitCode}
                onChange={(e) => setKudosUnitCode(e.target.value)}
                placeholder="e.g. B360U-AI"
              />
            )}
          </div>
          <button
            className="btn btn--secondary"
            type="button"
            onClick={() => fetchKudosAnalytics(kudosUnitCode)}
          >
            Apply unit filter
          </button>
          <button
            className="btn btn--ghost"
            type="button"
            onClick={() => {
              setKudosUnitCode("");
              fetchKudosAnalytics("");
            }}
          >
            Clear
          </button>
          {kudosUnitCode ? (
            <span className="pill" title="Current analytics scope">
              Filtered by: {kudosUnitCode}
            </span>
          ) : null}
        </div>
      </div>

      {showIntroPopup && (
        <div className="nudgeModalOverlay">
          <div className="nudgeModal">
            <div className="nudgeModal__header">
              <h2>Welcome to Smart Nudges! </h2>
            </div>
            <div className="nudgeModal__content">
              <p>Not sure what Nudges are? We've got you covered!</p>
              <p>
                <strong>Nudges</strong> are friendly, AI-powered reminders that help you stay engaged with your team.
                Whether it's an upcoming work anniversary, a mutual holiday, or a past project win,
                we'll bring the right context to your attention.
              </p>
              <p>
                If you don't have enough data yet, don't worry! Once you collaborate more,
                you'll see helpful suggestions here. You can then take action like sending a personalized greeting card with just one click.
              </p>
            </div>
            <div className="nudgeModal__actions">
              <button
                className="btn btn--secondary"
                onClick={closeIntroPopup}
              >
                Got it, thanks!
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card nudge-card">
        <div className="card__header">
          <h2 className="card__title">
            <span className="blinking-dot"></span>
            Actionable Nudges
          </h2>
          <p className="card__subtitle">Engagement suggestions based on milestones and shared wins.</p>
        </div>
        <div className="card__body">
          {nudges.length > 0 ? (
            <div className="nudgeList">
              {nudges.map((nudge) => (
                <div key={nudge.id} className={`nudgeItem nudgeItem--${nudge.type}`}>
                  <div className="nudgeItem__icon">
                    {nudge.type === 'handshake_nudge' && <UsersIcon className="icon" />}
                    {nudge.type === 'holiday_nudge' && <CalendarIcon className="icon" />}
                    {nudge.type === 'tuesday_surprise' && <SparklesIcon className="icon" />}
                    {!['handshake_nudge', 'holiday_nudge', 'tuesday_surprise'].includes(nudge.type) && <SparklesIcon className="icon" />}
                  </div>
                  <div className="nudgeItem__content">
                    <p className="nudgeItem__message">{nudge.message}</p>
                    <button
                      className="btn btn--primary btn--sm nudgeItem__action"
                      onClick={() => handleNudge(nudge)}
                    >
                      {nudge.action === 'approve_memory' ? 'Review Milestone' : 'Send Greeting'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="nudgeEmptyState">
              <img src="/images/empty_nudges.png" alt="No Nudges" />
              <p className="nudgeEmptyState__title">All caught up!</p>
              <p className="nudgeEmptyState__hint">
                As you collaborate more, we'll suggest smart actions here like sending a customized greeting card for an upcoming work anniversary or recognizing a shared project win.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="card anniversary-card">
        <div className="card__header">
          <h2 className="card__title">
            <CalendarIcon className="icon" />
            Upcoming work anniversaries
          </h2>
          <p className="card__subtitle">
            Employees celebrating a work anniversary in the next 14 days.
          </p>
        </div>
        <div className="card__body">
          {upcomingAnniversaries.length > 0 ? (
            <ul className="anniversaryList">
              {upcomingAnniversaries.map((a) => (
                <li key={a.user_id} className="anniversaryItem">
                  <div className="anniversaryItem__meta">
                    <div className="anniversaryItem__name">{a.name}</div>
                    <div className="anniversaryItem__hint">
                      {formatAnniversaryDate(a.anniversary_date)} · {a.years_of_service}{" "}
                      {a.years_of_service === 1 ? "year" : "years"}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    onClick={() => handleAnniversaryGreeting(a)}
                  >
                    Send greeting
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="anniversaryEmpty">
              No anniversaries coming up in the next 14 days.
            </p>
          )}
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card__header">
            <h2 className="card__title">Employees added</h2>
            <p className="card__subtitle">Monthly adds (last 6 months)</p>
          </div>
          <div className="card__body">
            <div className="dashChart">
              <svg width="100%" height="240" viewBox="0 0 520 240">
                <defs>
                  <linearGradient id="area" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="rgba(16,185,129,0.20)" />
                    <stop offset="100%" stopColor="rgba(16,185,129,0.02)" />
                  </linearGradient>
                </defs>

                {/* axes */}
                <line x1="50" y1="200" x2="470" y2="200" className="axis" />
                <line x1="50" y1="40" x2="50" y2="200" className="axis" />

                {/* grid */}
                {[0, 1, 2, 3].map((i) => (
                  <line
                    key={i}
                    x1="50"
                    y1={40 + i * 40}
                    x2="470"
                    y2={40 + i * 40}
                    className="gridLine"
                  />
                ))}

                {/* area */}
                <polygon
                  points={[
                    ...chartData.map((d, idx) => {
                      const x = 50 + idx * (420 / (chartData.length - 1));
                      const y = 200 - 40 - ((Number(d.count) || 0) / maxCount) * 120;
                      return `${x},${y}`;
                    }),
                    "470,200",
                    "50,200",
                  ].join(" ")}
                  fill="url(#area)"
                />

                {/* line */}
                <polyline
                  points={chartData
                    .map((d, idx) => {
                      const x = 50 + idx * (420 / (chartData.length - 1));
                      const y = 200 - 40 - ((Number(d.count) || 0) / maxCount) * 120;
                      return `${x},${y}`;
                    })
                    .join(" ")}
                  fill="none"
                  stroke="rgba(16,185,129,0.95)"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />

                {/* dots + values */}
                {chartData.map((d, idx) => {
                  const x = 50 + idx * (420 / (chartData.length - 1));
                  const y = 200 - 40 - ((Number(d.count) || 0) / maxCount) * 120;
                  return (
                    <g key={idx}>
                      <circle cx={x} cy={y} r="5" className="chartPoint" />
                      <text x={x} y={y - 12} textAnchor="middle" className="chartValue">
                        {Number(d.count) || 0}
                      </text>
                    </g>
                  );
                })}

                {/* x labels */}
                {chartData.map((d, idx) => {
                  const x = 50 + idx * (420 / (chartData.length - 1));
                  return (
                    <text key={idx} x={x} y={226} textAnchor="middle" className="chartLabel">
                      {d.month}
                    </text>
                  );
                })}
              </svg>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card__header">
            <h2 className="card__title">Upcoming festival calendar</h2>
            <p className="card__subtitle">Next 30 days</p>
          </div>
          <div className="card__body">
            {upcomingFestivals.length > 0 ? (
              <div className="festivalList">
                {upcomingFestivals.map((festival, idx) => {
                  const festivalDate = new Date(festival.date);
                  const daysUntil = Math.ceil(
                    (festivalDate - new Date()) / (1000 * 60 * 60 * 24)
                  );
                  return (
                    <div className="festivalRow" key={idx}>
                      <div className="festivalRow__date">
                        {festivalDate.toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })}
                      </div>
                      <div className="festivalRow__meta">
                        <div className="festivalRow__name">{festival.name}</div>
                        <div className="festivalRow__hint">
                          {daysUntil === 0
                            ? "Today"
                            : `${daysUntil} day${daysUntil === 1 ? "" : "s"} away`}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="emptyState">No upcoming festivals in the next 30 days.</div>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default Dashboard;
